/**
 * LogsTable - Table component for displaying log entries
 */
import React, { ReactElement, useState } from 'react'
import { Table, Tag, Flex, Button } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  BugOutlined,
} from '@ant-design/icons'
import { Resizable } from 'react-resizable'
import type { ResizeCallbackData } from 'react-resizable'
import type { Log } from 'types/search/logs'
import 'react-resizable/css/styles.css'
import styles from './LogsTable.module.scss'

export interface LogsTableProps {
  logs: Log[]
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}

/**
 * Renders a colored tag with icon for log level
 */
const renderLevelTag = (level: string): ReactElement => {
  const config = {
    error: { color: 'red', icon: <ExclamationCircleOutlined /> },
    warn: { color: 'orange', icon: <WarningOutlined /> },
    info: { color: 'blue', icon: <InfoCircleOutlined /> },
    debug: { color: 'default', icon: <BugOutlined /> },
  }
  const { color, icon } = config[level as keyof typeof config] || config.debug

  return (
    <Tag color={color} icon={icon}>
      {level.toUpperCase()}
    </Tag>
  )
}

/**
 * Formats timestamp to localized string
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)
  return date.toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  })
}

/**
 * Extract level from data
 */
const extractLevel = (log: Log): string => {
  return (log.data?.level as string) || 'info'
}

/**
 * Extract message from data
 */
const extractMessage = (log: Log): string => {
  return (log.data?.message as string) || ''
}

/**
 * ResizableTitle component for column resizing
 */
interface ResizableTitleProps extends React.HTMLAttributes<HTMLElement> {
  onResize?: (
    e: React.SyntheticEvent<Element>,
    data: ResizeCallbackData,
  ) => void
  width?: number
}

const ResizableTitle: React.FC<ResizableTitleProps> = (props) => {
  const { onResize, width, ...restProps } = props

  if (!width) {
    return <th {...restProps} />
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation()
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  )
}

/**
 * LogsTable component - displays log entries with expandable rows for metadata
 */
const LogsTable = (props: LogsTableProps): ReactElement => {
  const { logs, hasMore, isFetchingMore, onLoadMore } = props
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(
    [],
  )

  // Column widths state for resizing
  const [columns, setColumns] = useState<ColumnsType<Log>>([
    {
      title: 'Date/Time',
      dataIndex: 'dateTime',
      key: 'dateTime',
      width: 220,
      render: (dateTime: string) => formatTimestamp(dateTime),
    },
    {
      title: 'Level',
      key: 'level',
      width: 120,
      render: (_, record: Log) => renderLevelTag(extractLevel(record)),
    },
    {
      title: 'Message',
      key: 'message',
      ellipsis: true,
      render: (_, record: Log) => extractMessage(record),
    },
  ])

  // Handle column resize
  const handleResize =
    (index: number) =>
    (_e: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newColumns = [...columns]
      newColumns[index] = {
        ...newColumns[index],
        width: size.width,
      }
      setColumns(newColumns)
    }

  // Merge columns with resize handlers
  const mergedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column: ColumnType<Log>) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }))

  const expandedRowRender = (record: Log): ReactElement => {
    return (
      <Flex vertical gap={8} className={styles.expandedRow}>
        <h4 className={styles.expandedRowTitle}>Complete Log JSON:</h4>
        <pre className={styles.jsonViewer}>
          {JSON.stringify(record, null, 2)}
        </pre>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16} className={styles.container}>
      <Table<Log>
        columns={mergedColumns}
        dataSource={logs}
        rowKey="id"
        size="small"
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
        }}
        pagination={false}
        scroll={{ y: 'calc(100vh - 500px)' }}
      />

      {hasMore && (
        <Flex justify="center" className={styles.loadMoreContainer}>
          <Button
            type="primary"
            loading={isFetchingMore}
            onClick={onLoadMore}
            size="large"
          >
            {isFetchingMore ? 'Loading more logs...' : 'Load More'}
          </Button>
        </Flex>
      )}

      {logs.length > 0 && !hasMore && (
        <Flex justify="center" className={styles.noMoreLogs}>
          No more logs to load
        </Flex>
      )}
    </Flex>
  )
}

export default LogsTable
